import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getOrderPipeline,
  getAccountSnapshot,
  getRecentOrders,
} from '../../../api/customerDashboardApi.js';
import { getUser } from '../../../auth.js';
import { useRetryFetch } from '../hooks/useRetryFetch.js';
import StatusBadge from '../components/StatusBadge.jsx';
import { formatCurrency, formatDate } from '../utils/format.js';
import Spinner from '../components/Spinner.jsx';
import WarningIcon from '../components/WarningIcon.jsx';
import Tooltip from '../components/Tooltip.jsx';
import './Dashboard.css';

const TODAY = new Date().toLocaleDateString('en-US', {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

function PipelineCard() {
  const { data, loading, error, errorMessage, manualRetries, retry } = useRetryFetch(() => getOrderPipeline(), []);

  return (
    <div className="customer-portal-card customer-portal-pipeline">
      <div className="customer-portal-pipeline__header">
        <div className="customer-portal-pipeline__title">
          Order Pipeline {data && <span>&middot; {data.totalOrders} orders total</span>}
        </div>
        {data && <span className="customer-portal-pipeline__window">{data.windowLabel}</span>}
      </div>

      {loading && <div className="customer-portal-card__loader"><Spinner size={32} /></div>}

      {error && (
        <div className="customer-portal-card__error">
          <WarningIcon />
          <span>{errorMessage || "Couldn't load pipeline"}</span>
          {manualRetries > 0 && (
            <button
              onClick={retry}
              className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {data && (
        <>
          <div className="customer-portal-pipeline__scroll">
          <div className="customer-portal-pipeline__row">
            <div className="customer-portal-pipeline__happy-path">
              {data.stages.map((stage, i) => (
                <div
                  key={stage.key}
                  className={`customer-portal-pipeline__stage customer-portal-pipeline__stage--${stage.key}`}
                  style={{
                    borderRadius:
                      i === 0
                        ? '10px 0 0 10px'
                        : i === data.stages.length - 1
                          ? '0 10px 10px 0'
                          : '0',
                  }}
                >
                  <Tooltip as="div" className="customer-portal-pipeline__stage-label">{stage.label}</Tooltip>
                  <Tooltip as="div" className="customer-portal-pipeline__stage-count">{stage.count}</Tooltip>
                  <Tooltip as="div" className="customer-portal-pipeline__stage-meta">{stage.meta}</Tooltip>
                  {i < data.stages.length - 1 && (
                    <div className="customer-portal-pipeline__stage-chevron">
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="customer-portal-pipeline__divider">
              <div className="customer-portal-pipeline__divider-line" />
              <div className="customer-portal-pipeline__divider-label">off-track</div>
              <div className="customer-portal-pipeline__divider-line" />
            </div>

            <div className="customer-portal-pipeline__offtrack">
              {data.offTrack.map((item) => (
                <div key={item.key} className={`customer-portal-pipeline__offtrack-card customer-portal-pipeline__offtrack-card--${item.key}`}>
                  <div className="customer-portal-pipeline__offtrack-text">
                    <Tooltip as="div" className="customer-portal-pipeline__offtrack-label">{item.label}</Tooltip>
                    <Tooltip as="div" className="customer-portal-pipeline__offtrack-count">{item.count}</Tooltip>
                  </div>
                  <div className="customer-portal-pipeline__offtrack-icon">
                    {item.key === 'rejected' ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>

          <div className="customer-portal-pipeline__progress">
            <div className="customer-portal-pipeline__progress-track">
              {data.stages.map((stage) => (
                <div
                  key={stage.key}
                  className={`customer-portal-pipeline__progress-fill customer-portal-pipeline__progress-fill--${stage.key}`}
                  style={{ width: `${(stage.count / data.totalOrders) * 100}%` }}
                />
              ))}
            </div>
            <span className="customer-portal-pipeline__progress-label">
              {data.activeCount} active &middot; {data.offTrackCount} off-track
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function RecentOrdersCard() {
  const navigate = useNavigate();
  const { data, loading, error, errorMessage, manualRetries, retry } = useRetryFetch(() => getRecentOrders(), []);

  return (
    <div className="customer-portal-card customer-portal-orders-table">
      <div className="customer-portal-orders-table__header">
        <div>
          <div className="customer-portal-orders-table__title">Recent Orders</div>
          <div className="customer-portal-orders-table__subtitle">Your latest activity</div>
        </div>
        <button className="customer-portal-btn customer-portal-btn--dark" onClick={() => navigate('/customer/orders')}>
          View All &rarr;
        </button>
      </div>

      {loading && <div className="customer-portal-card__loader"><Spinner size={32} /></div>}

      {error && (
        <div className="customer-portal-card__error">
          <WarningIcon />
          <span>{errorMessage || "Couldn't load recent orders"}</span>
          {manualRetries > 0 && (
            <button
              onClick={retry}
              className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {data && (
        <div className="customer-portal-orders-table__scroll">
          <div className="customer-portal-orders-table__cols">
            <span className="customer-portal-orders-table__left">Order ID</span>
            <span className="customer-portal-orders-table__left">PO Number</span>
            <span className="customer-portal-orders-table__center">Date</span>
            <span className="customer-portal-orders-table__right">Total</span>
            <span className="customer-portal-orders-table__center">Status</span>
            <span className="customer-portal-orders-table__center">Actions</span>
          </div>
          {data.length === 0 ? (
            <div className="customer-portal-orders-table__empty">No recent orders</div>
          ) : (
            data.map((order) => (
              <div className="customer-portal-orders-table__row" key={order.id}>
                <Tooltip as="span" className="customer-portal-orders-table__id customer-portal-orders-table__left">#{order.id}</Tooltip>
                <Tooltip as="span" className="customer-portal-orders-table__po customer-portal-orders-table__left">{order.poNumber ?? '—'}</Tooltip>
                <Tooltip as="span" className="customer-portal-orders-table__date customer-portal-orders-table__center">{formatDate(order.date)}</Tooltip>
                <Tooltip as="span" className="customer-portal-orders-table__total customer-portal-orders-table__right">{formatCurrency(order.total, order.currency)}</Tooltip>
                <StatusBadge status={order.status} />
                <button className="customer-portal-btn customer-portal-btn--outline" onClick={() => navigate(`/customer/orders/${order.id}`)}>
                  View
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SnapshotCard() {
  const { data, loading, error, errorMessage, manualRetries, retry } = useRetryFetch(() => getAccountSnapshot(), []);

  return (
    <div className="customer-portal-card customer-portal-panel">
      <div className="customer-portal-panel__header">
        <div className="customer-portal-panel__title">Account Snapshot</div>
        {data && <Tooltip as="span" className="customer-portal-panel__meta">{data.entity} &middot; {data.customerCode}</Tooltip>}
      </div>

      {loading && <div className="customer-portal-card__loader"><Spinner size={32} /></div>}

      {error && (
        <div className="customer-portal-card__error">
          <WarningIcon />
          <span>{errorMessage || "Couldn't load account snapshot"}</span>
          {manualRetries > 0 && (
            <button
              onClick={retry}
              className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {data && (
        <>
          <div className="customer-portal-snapshot-credit">
            <div className="customer-portal-snapshot-credit__row">
              <span className="customer-portal-snapshot-credit__label">Credit Used</span>
              <Tooltip as="span" className="customer-portal-snapshot-credit__value">
                {formatCurrency(data.creditUsed, data.currency)}{' '}
                <span className="customer-portal-snapshot-credit__of">of {formatCurrency(data.creditLimit, data.currency)}</span>
              </Tooltip>
            </div>
            <div className="customer-portal-snapshot-credit__track">
              <div className="customer-portal-snapshot-credit__fill" style={{ width: `${Math.round((data.creditUsed / data.creditLimit) * 100)}%` }} />
            </div>
            <Tooltip as="div" className="customer-portal-snapshot-credit__note">
              {Math.round(data.creditLimit === 0 ? 100 : (data.creditUsed / data.creditLimit) * 100)}% used &middot; {formatCurrency(data.creditLimit < data.creditUsed ? 0 : data.creditLimit - data.creditUsed, data.currency)} available
            </Tooltip>
          </div>
          <div className="customer-portal-snapshot-divider" />
          <div className="customer-portal-snapshot-grid">
            <div className="customer-portal-snapshot-tile">
              <div className="customer-portal-snapshot-tile__label">Outstanding</div>
              <Tooltip as="div" className="customer-portal-snapshot-tile__value customer-portal-snapshot-tile__value--pink">
                {formatCurrency(data.outstanding, data.currency)}
              </Tooltip>
            </div>
            <div className="customer-portal-snapshot-tile">
              <div className="customer-portal-snapshot-tile__label">YTD Spend</div>
              <Tooltip as="div" className="customer-portal-snapshot-tile__value">{formatCurrency(data.ytdSpend, data.currency)}</Tooltip>
            </div>
          </div>
          <div className="customer-portal-snapshot-terms">
            <div>
              <div className="customer-portal-snapshot-terms__label">Terms</div>
              <Tooltip as="div" className="customer-portal-snapshot-terms__value">{data.terms}</Tooltip>
            </div>
            <div>
              <div className="customer-portal-snapshot-terms__label">Next Due</div>
              <Tooltip as="div" className="customer-portal-snapshot-terms__value">{data.nextDue}</Tooltip>
            </div>
            <div>
              <div className="customer-portal-snapshot-terms__label">Currency</div>
              <Tooltip as="div" className="customer-portal-snapshot-terms__value">{data.currencyCode}</Tooltip>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    setCustomer(getUser());
  }, []);

  return (
    <div className="customer-portal-page-content">
      <div className="customer-portal-dash-welcome">
        <div>
          <div className="customer-portal-dash-welcome__title">{greeting()}, {customer?.name ?? 'Loading...'}</div>
          <div className="customer-portal-dash-welcome__meta">
            {customer?.email} &nbsp;&middot;&nbsp; Entity: <strong>{customer?.entity}</strong>
            &nbsp;&middot;&nbsp; Code: <strong>{customer?.customerCode}</strong> &nbsp;&middot;&nbsp; {TODAY}
          </div>
        </div>
        <div className="customer-portal-dash-welcome__actions">
          <button className="customer-portal-btn customer-portal-btn--ghost">Repeat Order</button>
          <button className="customer-portal-btn customer-portal-btn--primary" onClick={() => navigate('/customer/orders/new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Order
          </button>
        </div>
      </div>

      <PipelineCard />
      <div className="customer-portal-dash-panels-row">
        <RecentOrdersCard />
        <SnapshotCard />
      </div>
    </div>
  );
}
